// src/components/staff/mobile/MobileStaffClients.jsx
// Visible to: all staff (20+) — read-only client + aircraft overview

import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plane, Search, ChevronDown, ChevronUp, Phone, Mail, MapPin, CreditCard } from 'lucide-react';
import { getAllClients, getClientAircraft } from '../../../services/staffApi';

const GLOBAL_STYLE = `@keyframes spin { to { transform: rotate(360deg) } }`;

const ClientCard = ({ client }) => {
  const [expanded, setExpanded] = useState(false);
  const [aircraft, setAircraft] = useState([]);
  const [loadingAc, setLoadingAc] = useState(false);

  const handleExpand = async () => {
    if (!expanded && aircraft.length === 0) {
      setLoadingAc(true);
      try { const r = await getClientAircraft(client.id); setAircraft(r.data ?? []); }
      catch (e) { console.error(e); }
      finally { setLoadingAc(false); }
    }
    setExpanded(e => !e);
  };

  const initials = `${client.first_name?.[0] ?? ''}${client.last_name?.[0] ?? ''}`.toUpperCase();

  return (
    <div style={{ background: '#1e293b', borderRadius: 18, border: '0.5px solid #334155', overflow: 'hidden' }}>
      <button onClick={handleExpand} style={{ width: '100%', background: 'none', border: 'none', padding: '14px 16px', textAlign: 'left', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 13, background: 'linear-gradient(135deg, #1d4ed8, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
            {initials || <Users size={18} color="#fff" />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {client.first_name} {client.last_name}
            </div>
            {client.company_name && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {client.company_name}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <span style={{ fontSize: 10, color: '#475569', background: '#0f172a', padding: '3px 8px', borderRadius: 20 }}>
              {client.aircraft_count ?? '?'} aircraft
            </span>
            {expanded ? <ChevronUp size={15} color="#475569" /> : <ChevronDown size={15} color="#475569" />}
          </div>
        </div>
      </button>

      {expanded && (
        <div style={{ borderTop: '0.5px solid #334155', padding: '12px 16px', background: '#0f172a17' }}>
          {/* Contact info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {client.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Mail size={13} color="#475569" />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{client.email}</span>
              </div>
            )}
            {client.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Phone size={13} color="#475569" />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{client.phone}</span>
              </div>
            )}
            {client.address && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapPin size={13} color="#475569" />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{client.address}</span>
              </div>
            )}
            {client.balance !== undefined && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CreditCard size={13} color={client.balance > 0 ? '#ef4444' : '#22c55e'} />
                <span style={{ fontSize: 12, color: client.balance > 0 ? '#fca5a5' : '#86efac', fontWeight: 600 }}>
                  Balance: ${Math.abs(client.balance ?? 0).toLocaleString()}
                  {client.balance > 0 ? ' outstanding' : client.balance < 0 ? ' credit' : ' — clear'}
                </span>
              </div>
            )}
          </div>

          {/* Aircraft */}
          <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Aircraft</div>
          {loadingAc ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
              <div style={{ width: 18, height: 18, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
          ) : aircraft.length === 0 ? (
            <p style={{ fontSize: 12, color: '#475569' }}>No aircraft registered</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {aircraft.map(ac => (
                <div key={ac.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#1e293b', borderRadius: 12, border: '0.5px solid #334155' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Plane size={14} color="#818cf8" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>{ac.tail_number}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{ac.manufacturer} {ac.model} {ac.year ? `(${ac.year})` : ''}</div>
                  </div>
                  <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 20, flexShrink: 0, background: ac.status === 'active' ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.15)', color: ac.status === 'active' ? '#22c55e' : '#64748b', fontWeight: 600 }}>
                    {ac.status ?? 'Active'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MobileStaffClients = () => {
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getAllClients(); setClients(r.data ?? []); setFiltered(r.data ?? []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      q ? clients.filter(c =>
        `${c.first_name} ${c.last_name} ${c.company_name ?? ''}`.toLowerCase().includes(q)
      ) : clients
    );
  }, [search, clients]);

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <Search size={16} color="#475569" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search clients…"
          style={{ width: '100%', background: '#1e293b', border: '0.5px solid #334155', borderRadius: 14, padding: '12px 14px 12px 40px', color: '#f1f5f9', fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ fontSize: 12, color: '#475569', marginBottom: 12 }}>{filtered.length} client{filtered.length !== 1 ? 's' : ''}</div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <Users size={36} color="#334155" style={{ marginBottom: 12 }} />
          <p style={{ color: '#475569' }}>No clients found</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(c => <ClientCard key={c.id} client={c} />)}
        </div>
      )}
    </div>
  );
};

export default MobileStaffClients;